import { keyBy, pickBy, mapValues } from 'lodash';
import axios from 'axios';
import { types, flow, Instance, cast } from 'mobx-state-tree';
import * as R from 'ramda';

import * as PreferencesAPI from 'src/services/preferences';
import { toast } from 'src/components';

const ThemeEntityModel = types.model('ThemeEntityModel', {
  theme_id: types.union(types.number, types.string),
  name: types.string,
  level: types.string,
  on_moderation: types.optional(types.boolean, false),
  children: types.maybeNull(types.array(types.frozen()))
});

type Level = 'main_theme' | 'sub_theme' | 'focus' | 'skills_family';

interface IThemes {
  parent_id?: number;
  level?: Level;
  on_moderation?: 'all' | 'true' | 'false';
}

const ThemesModel = types
  .model('ThemesModel', {
    main_themes: types.maybeNull(types.map(ThemeEntityModel)),
    sub_themes: types.maybeNull(types.map(ThemeEntityModel)),
    skills_families: types.maybeNull(types.map(ThemeEntityModel)),
    focuses: types.maybeNull(types.map(ThemeEntityModel)),
    children: types.maybeNull(types.map(types.frozen()))
  })
  .actions(self => ({
    addToParentChildren: (fieldName, parentId, payload) => {
      if (self[fieldName].get(parentId).children) {
        self[fieldName].get(parentId).children.push(payload);
      } else {
        const ancestor = self[fieldName].get(parentId);
        self[fieldName].set(parentId, { ...ancestor, children: [payload] });
      }
    },
    addToTheme: (fieldName, item) => {
      self[fieldName].set(item.id, item);
    }
  }));

export const OuterThemesModel = types
  .model({
    themesIsFetching: 0,
    themes: types.maybeNull(ThemesModel)
  })
  .actions(self => {
    const CancelToken = axios.CancelToken;
    let source = CancelToken.source();
    return {
      abort: function() {
        source.cancel('Operation canceled by the user.');
        source = CancelToken.source();
      },
      // layer to handle new api form
      // it's like has backwardcompatibility that's why it such a mess
      // it fetches main theme level object and update themesObj passing children tree
      // then assing mobx state tree to themesObj if mobx tree was empty
      // otherwise it merges mobx tree and child tree which is themesObj at this point
      // (above happens at return line)
      // if u wonder how to fetch everything for 1 main theme then it's just fetchThemes({parent_id: mainThemeId, level: 'main_theme'})
      // entities -> main_theme, children_tree -> sub_themes, focuses etc.
      fetchThemes: flow(function*(params: IThemes) {
        const createThemeObject = (main_themes, child_tree, level: Level) => {
          try {
            const entities = main_themes.map(entity => entity);
            let themesObj = {
              main_themes: null,
              sub_themes: null,
              skills_families: null,
              focuses: null,
              children: null
            };

            if (!R.isEmpty(child_tree)) {
              themesObj = {
                ...themesObj,
                ...castThemesToObject(child_tree),
                children: keyBy(
                  [
                    ...entities,
                    ...child_tree.sub_theme,
                    ...(child_tree.focus || []),
                    ...(child_tree.skills_family || [])
                  ],
                  'id'
                )
              };
            }

            themesObj.main_themes = keyBy(
              renameThemeIdProperty(entities).map(theme => {
                return {
                  ...theme,
                  children:
                    params.parent_id === theme.theme_id
                      ? Object.values(child_tree.sub_theme).map(({ id }) => id)
                      : self.themes && self.themes.main_themes?.get(String(theme.theme_id))?.children
                      ? self.themes.main_themes.get(String(theme.theme_id)).children
                      : null
                };
              }),
              'theme_id'
            );

            return self.themes === null
              ? themesObj
              : {
                  main_themes: self.themes.main_themes.merge(themesObj.main_themes),
                  sub_themes: self.themes.sub_themes.merge(themesObj.sub_themes),
                  skills_families: self.themes.skills_families.merge(themesObj.skills_families),
                  focuses: self.themes.focuses.merge(themesObj.focuses),
                  children:
                    self.themes.children === null ? themesObj.children : self.themes.children.merge(themesObj.children)
                };
          } catch (error) {
            toast.error(JSON.stringify(error));
          }
        };
        self.themesIsFetching = self.themesIsFetching + 1;

        try {
          const { entities, child_tree } = yield PreferencesAPI.getThemes({
            params,
            cancelToken: source.token
          });

          self.themes = cast(createThemeObject(entities, child_tree, params.level));
        } finally {
          self.themesIsFetching = self.themesIsFetching - 1;
        }
      }),
      fetchAllThemes: flow(function*(params = {}) {
        self.themesIsFetching = self.themesIsFetching + 1;

        try {
          const { entities } = yield PreferencesAPI.getThemes({
            params: { ...params, level: 'main_theme' },
            cancelToken: source.token
          });
          yield Promise.all(
            renameThemeIdProperty(entities).map(async ({ theme_id }, index) => {
              await self.fetchThemes({ parent_id: theme_id, level: 'main_theme' });
            })
          );
        } finally {
          self.themesIsFetching = self.themesIsFetching - 1;
        }
      }),
      updateThemes: function(themes) {
        try {
          self.themes = castThemesToObjectLegacy(themes);
        } catch (error) {}
      }
    };
  })
  .views(self => ({
    get themesById() {
      if (self.themes === null)
        return {
          main_themes: [],
          sub_themes: [],
          skills_families: [],
          focuses: [],
          allThemes: {},
          children: []
        };
      return {
        main_themes: Object.values(self.themes?.main_themes.toJSON()).sort((a, b) => a.name.localeCompare(b.name)),
        sub_themes: Object.values(self.themes?.sub_themes.toJSON()),
        skills_families: Object.values(self.themes?.skills_families.toJSON()),
        focuses: Object.values(self.themes?.focuses.toJSON()),
        allThemes: {
          ...self.themes?.main_themes.toJSON(),
          ...self.themes?.sub_themes.toJSON(),
          ...self.themes?.skills_families.toJSON(),
          ...self.themes?.focuses.toJSON()
        }
      };
    },
    get themesInLegacyShape() {
      if (self.themes === null)
        return {
          main_themes: [],
          sub_themes: [],
          skills_families: [],
          focuses: [],
          allThemes: {},
          children: []
        };

      return {
        main_themes: Object.values(self.themes?.main_themes.toJSON()).map(item => ({ ...item, id: item.theme_id })),
        sub_themes: Object.values(self.themes?.sub_themes.toJSON()).map(item => ({ ...item, id: item.theme_id })),
        skills_families: Object.values(self.themes?.skills_families.toJSON()).map(item => ({
          ...item,
          id: item.theme_id
        })),
        focuses: Object.values(self.themes?.focuses.toJSON()).map(item => ({ ...item, id: item.theme_id })),
        allThemes: {
          ...mapValues(self.themes?.main_themes.toJSON(), item => ({ ...item, id: item.theme_id })),
          ...mapValues(self.themes?.sub_themes.toJSON(), item => ({ ...item, id: item.theme_id })),
          ...mapValues(self.themes?.skills_families.toJSON(), item => ({ ...item, id: item.theme_id })),
          ...mapValues(self.themes?.focuses.toJSON(), item => ({ ...item, id: item.theme_id }))
        },
        children: self.themes?.children === null ? null : self.themes?.children.toJSON()
      };
    },
    getThemesByUserPreferences(userPreferences: any[]) {
      return {
        main_themes: self.themes?.main_themes.toJSON(),
        sub_themes: pickBy(
          self.themes?.sub_themes.toJSON(),
          item =>
            !item.on_moderation || userPreferences.some(userPreference => userPreference.sub_themes[item.theme_id])
        ),
        skills_families: self.themes?.skills_families.toJSON(),
        focuses: pickBy(
          self.themes?.focuses.toJSON(),
          item => !item.on_moderation || userPreferences.some(userPreference => userPreference.focuses[item.theme_id])
        )
      };
    }
  }));

const castThemesToObject = themes => ({
  ...(themes.main_theme && { main_themes: keyBy(renameThemeIdProperty(themes.main_theme), 'theme_id') }),
  sub_themes: keyBy(
    renameThemeIdProperty(themes.sub_theme).map(({ children, ...other }) => ({
      ...other,
      children: children?.map(({ id }) => id) || null
    })),
    'theme_id'
  ),
  ...(themes.skills_family
    ? {
        skills_families: keyBy(
          renameThemeIdProperty(themes.skills_family).map(({ children, ...other }) => ({
            ...other,
            children: children?.map(({ id }) => id) || null
          })),
          'theme_id'
        )
      }
    : { skills_families: {} }),
  ...(themes.focus
    ? {
        focuses: keyBy(renameThemeIdProperty(themes.focus), 'theme_id')
      }
    : { focus: {} }),
  children: themes.children || null
});

const castThemesToObjectLegacy = themes => ({
  main_themes: keyBy(renameThemeIdProperty(themes.main_themes), 'theme_id'),
  sub_themes: keyBy(renameThemeIdProperty(themes.sub_themes), 'theme_id'),
  skills_families: keyBy(renameThemeIdProperty(themes.skills_families), 'theme_id'),
  focuses: keyBy(renameThemeIdProperty(themes.focuses), 'theme_id'),
  children: themes.children
});

const renameThemeIdProperty = themes => themes.map(({ id, ...theme }) => ({ ...theme, theme_id: id }));

export type IThemesModel = Instance<typeof ThemesModel>;
